import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { ResendOTP } from "otp-input-react";
import { toast } from "react-toastify";
import { LoginAPI, setAuthToken, removeAuthToken } from "../api";
import { getUser } from "../../redux/modules/userSlice";
import { useAppDispatch } from "../../redux/hooks";
import AuthLayout from "./AuthLayout";
import AuthTextField from "./AuthTextField";
import AuthPrimaryButton from "./AuthPrimaryButton";
import {
  authActionsSx,
  authFormStackSx,
  authFormTitleSx,
  authLinkButtonSx,
} from "./authStyles";

var otpSchema = Yup.object({
  otp: Yup.string()
    .required("OTP is required")
    .matches(/^[0-9]{6}$/, "Invalid OTP"),
});

function Otpverify() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  var email = location.state && location.state.email;
  const [resendLoading, setResendLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(
    function () {
      if (!email) {
        navigate("/login", { replace: true });
      }
    },
    [email, navigate]
  );

  const formik = useFormik({
    initialValues: { otp: "" },
    validationSchema: otpSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: function (values) {
      if (!email) {
        return;
      }
      setSubmitting(true);
      formik.setFieldError("otp", undefined);
      LoginAPI.verifyOTP({ email: email, otp: Number(values.otp) })
        .then(function (response) {
          setSubmitting(false);
          if (response.success) {
            var data = response.data;
            var token = response.token;
            if (!token) {
              formik.setFieldError("otp", "Authentication failed");
              formik.setFieldTouched("otp", true, false);
              return;
            }

            setAuthToken(token);
            dispatch(getUser(data));

            toast.success(response.message, {
              hideProgressBar: true,
              closeOnClick: true,
            });

            if (data.isSuperAdmin) {
              navigate("/AdminDashboard");
            } else {
              navigate("/", { state: { id: data._id } });
            }
          } else {
            removeAuthToken();
            formik.setFieldError(
              "otp",
              response.message || "Invalid OTP"
            );
            formik.setFieldTouched("otp", true, false);
          }
        })
        .catch(function (err) {
          setSubmitting(false);
          removeAuthToken();
          var apiMessage =
            err &&
            err.response &&
            err.response.data &&
            err.response.data.message
              ? err.response.data.message
              : "Invalid OTP";
          formik.setFieldError("otp", apiMessage);
          formik.setFieldTouched("otp", true, false);
          console.log(err);
        });
    },
  });

  function handleOtpChange(event) {
    var next = event.target.value.replace(/[^0-9]/g, "").slice(0, 6);
    formik.setFieldValue("otp", next, true);
    formik.setFieldTouched("otp", true, false);
  }

  function handleResendOtp() {
    if (!email || resendLoading) {
      return;
    }
    setResendLoading(true);
    formik.setFieldError("otp", undefined);
    LoginAPI.resendOTP({ email: email })
      .then(function (response) {
        setResendLoading(false);
        if (response.success) {
          toast.success(response.message || "OTP sent successfully", {
            hideProgressBar: true,
            closeOnClick: true,
          });
        } else {
          toast.error(response.message || "Unable to resend OTP", {
            hideProgressBar: true,
            closeOnClick: true,
          });
        }
      })
      .catch(function () {
        setResendLoading(false);
        toast.error("Unable to resend OTP", {
          hideProgressBar: true,
          closeOnClick: true,
        });
      });
  }

  function renderResendButton(buttonProps) {
    var remainingTime = buttonProps.remainingTime;
    var label =
      remainingTime !== 0
        ? "Resend OTP in " + remainingTime + "s"
        : resendLoading
        ? "Sending..."
        : "Resend OTP";
    return (
      <Button
        type="button"
        disabled={buttonProps.disabled || resendLoading}
        onClick={buttonProps.onClick}
        sx={Object.assign({}, authLinkButtonSx, {
          mt: 0.5,
          alignSelf: "flex-start",
        })}
      >
        {label}
      </Button>
    );
  }

  function renderResendTime() {
    return null;
  }

  var otpError =
    formik.touched.otp && formik.errors.otp ? formik.errors.otp : "";

  var canSubmit =
    !submitting &&
    formik.values.otp &&
    formik.values.otp.length === 6;

  if (!email) {
    return null;
  }

  return (
    <AuthLayout>
      <Typography component="h1" sx={authFormTitleSx}>
        LOGIN
      </Typography>

      <Box
        component="form"
        noValidate
        onSubmit={formik.handleSubmit}
        sx={authFormStackSx}
      >
        <AuthTextField
          id="otp-email"
          name="email"
          label="Email"
          type="email"
          value={email}
          disabled
        />

        <AuthTextField
          id="otp"
          name="otp"
          label="OTP"
          type="text"
          autoComplete="one-time-code"
          autoFocus
          placeholder="Enter OTP"
          value={formik.values.otp}
          onChange={handleOtpChange}
          onBlur={formik.handleBlur}
          error={Boolean(otpError)}
          helperText={otpError}
          inputProps={{ inputMode: "numeric", maxLength: 6 }}
        />

        <Box sx={authActionsSx}>
          <AuthPrimaryButton
            type="submit"
            disabled={!canSubmit}
            loading={submitting}
          >
            Login
          </AuthPrimaryButton>
          <Button
            type="button"
            onClick={function () {
              navigate("/login");
            }}
            sx={authLinkButtonSx}
          >
            Back to Login
          </Button>
        </Box>

        <ResendOTP
          maxTime={30}
          onResendClick={handleResendOtp}
          renderButton={renderResendButton}
          renderTime={renderResendTime}
        />
      </Box>
    </AuthLayout>
  );
}

export default Otpverify;
